import type { ButtonProps } from '@chakra-ui/react';
import { Button, Spinner } from '@chakra-ui/react';
import {
  currentWallet,
  getAddEthereumChainParameter,
  useDesiredChain,
} from '@cronos-app/wallet';
import { useIsAuthenticated } from '@ui/hooks/api/useIsAuthenticated';
import { useLogin } from '@ui/hooks/api/useLogin';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import { ensureHasProfile } from '@ui/utils/ensureHasProfile';
import React, { useState } from 'react';

import { useWalletModal } from './Wallet';

export const useAuthenticationStatus = () => {
  const isConnected = currentWallet.useIsConnected();
  const chainLayer = useGlobalChainLayer();
  const chainId = useDesiredChain(chainLayer);
  const isConnectedToSupportedChain =
    currentWallet.useIsConnectedToSupportedChain(chainLayer);
  const isAuthenticated = useIsAuthenticated().data;

  return {
    chainId,
    chainLayer,
    isConnected,
    isConnectedToSupportedChain,
    isAuthenticated,
  };
};

export const ButtonWithAuthentication: React.FC<
  ButtonProps & { overwriteContent?: boolean }
> = ({ overwriteContent = true, ...props }) => {
  const { chainId, isConnected, isConnectedToSupportedChain, isAuthenticated } =
    useAuthenticationStatus();
  const [waitingForConnection, setWaitingForConnection] = useState(false);
  const { mutateAsync: login, isLoading: isLoginLoading } = useLogin();

  if (!isConnected) {
    return (
      <Button
        {...props}
        onClick={() => useWalletModal.getState().openModal()}
        disabled={false}
      >
        {overwriteContent ? 'Connect' : props.children}
      </Button>
    );
  }

  if (!isConnectedToSupportedChain) {
    return (
      <Button
        {...props}
        disabled={waitingForConnection}
        onClick={async (e) => {
          setWaitingForConnection(true);
          try {
            await currentWallet.connect(getAddEthereumChainParameter(chainId));
            if (!props.disabled && isAuthenticated) props.onClick?.(e);
          } catch (e: unknown) {
            // ignore, probably user just didn't want to change network
          } finally {
            setWaitingForConnection(false);
          }
        }}
      >
        {overwriteContent ? 'Connect' : props.children}
      </Button>
    );
  }

  if (!isAuthenticated) {
    return (
      <Button
        {...props}
        disabled={isLoginLoading}
        onClick={async (e) => {
          await login();
          await ensureHasProfile(() => {
            if (!props.disabled) props.onClick?.(e);
          });
        }}
      >
        {isLoginLoading && <Spinner size="sm" />}
        {!isLoginLoading && overwriteContent && 'Login'}
        {!isLoginLoading && !overwriteContent && props.children}
      </Button>
    );
  }

  return <Button {...props} />;
};
